package com.rainbowclubs.rainbow.presentation.admin

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.rainbowclubs.rainbow.data.models.UserRole
import com.rainbowclubs.rainbow.presentation.theme.RainbowClubTheme
import com.rainbowclubs.rainbow.presentation.theme.accentBlue
import com.rainbowclubs.rainbow.presentation.theme.accentOrange
import com.rainbowclubs.rainbow.presentation.theme.transparent
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import rainbowclubs.composeapp.generated.resources.*

@Composable
fun ManageMembersScreen(
    clubId: String,
    role: UserRole,
    viewModel: ManageMembersViewModel = koinViewModel(parameters = { parametersOf(clubId, role) }),
    navigateTo: (ManageMembersViewModel.NavigationEvent) -> Unit,
) {
    val uiState by viewModel.uiState.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.navigationIntent.collect { intent ->
            navigateTo(intent)
        }
    }

    Scaffold(
        containerColor = MaterialTheme.colorScheme.transparent,
    ) {
        Box(
            modifier = Modifier
                .padding(it)
                .fillMaxSize(),
        ) {
            ManageMembersScreenContent(
                uiState = uiState,
                onBackClick = viewModel::onBackClick,
                onNewInvitationsClick = viewModel::onNewInvitationsClick,
                onExistingMembersClick = viewModel::onExistingMembersClick,
            )
        }
    }
}

@Composable
private fun ManageMembersScreenContent(
    uiState: ManageMembersUiState,
    onBackClick: () -> Unit,
    onNewInvitationsClick: () -> Unit,
    onExistingMembersClick: () -> Unit,
) {
    val actions = manageMemberActions(
        role = uiState.role,
        onNewInvitationsClick = onNewInvitationsClick,
        onExistingMembersClick = onExistingMembersClick,
    )

    Column(
        modifier = Modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState()),
    ) {
        ManageMembersHeader(onBackClick = onBackClick)

        Spacer(modifier = Modifier.height(52.dp))

        Row(
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Icon(
                painter = painterResource(Res.drawable.ic_dashboard_grid),
                contentDescription = stringResource(Res.string.member_actions_icon_cd),
                modifier = Modifier.size(24.dp),
                tint = MaterialTheme.colorScheme.primary,
            )
            Spacer(modifier = Modifier.width(16.dp))
            Text(
                text = stringResource(Res.string.member_actions),
                color = MaterialTheme.colorScheme.onSurface,
                style = MaterialTheme.typography.titleLarge,
            )
        }

        Spacer(modifier = Modifier.height(28.dp))

        Column(
            verticalArrangement = Arrangement.spacedBy(24.dp),
        ) {
            actions.forEach { action ->
                ManageMemberActionCard(action = action)
            }
        }

        Spacer(modifier = Modifier.height(36.dp))
    }
}

@Composable
private fun ManageMembersHeader(onBackClick: () -> Unit) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        IconButton(
            onClick = onBackClick,
            modifier = Modifier.align(Alignment.Top).size(24.dp),
        ) {
            Icon(
                painter = painterResource(Res.drawable.ic_arrow_back),
                contentDescription = stringResource(Res.string.back_icon_cd),
                modifier = Modifier.size(24.dp),
                tint = MaterialTheme.colorScheme.onBackground,
            )
        }

        Spacer(modifier = Modifier.width(16.dp))

        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = stringResource(Res.string.manage_members),
                color = MaterialTheme.colorScheme.onSurface,
                style = MaterialTheme.typography.titleLarge,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )

            Spacer(modifier = Modifier.height(8.dp))

            Text(
                text = stringResource(Res.string.manage_members_page_description),
                color = MaterialTheme.colorScheme.outline,
                style = MaterialTheme.typography.titleMedium,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
        }
    }
}

@Composable
private fun ManageMemberActionCard(action: ManageMemberAction) {
    Surface(
        onClick = action.onClick,
        modifier = Modifier
            .fillMaxWidth()
            .height(128.dp),
        shape = RoundedCornerShape(28.dp),
        color = action.accent.copy(alpha = 0.06f),
        border = BorderStroke(1.dp, action.accent.copy(alpha = 0.18f)),
    ) {
        Row(
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = 16.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier = Modifier
                    .size(72.dp)
                    .shadow(
                        elevation = 18.dp,
                        shape = RoundedCornerShape(24.dp),
                        ambientColor = action.accent.copy(alpha = 0.18f),
                        spotColor = action.accent.copy(alpha = 0.18f),
                    )
                    .background(MaterialTheme.colorScheme.surface, RoundedCornerShape(24.dp)),
                contentAlignment = Alignment.Center,
            ) {
                Box(
                    modifier = Modifier
                        .size(50.dp)
                        .background(
                            brush = Brush.linearGradient(
                                listOf(action.accent.copy(alpha = 0.82f), action.accent)
                            ),
                            shape = RoundedCornerShape(14.dp),
                        ),
                    contentAlignment = Alignment.Center,
                ) {
                    Icon(
                        painter = painterResource(action.icon),
                        contentDescription = stringResource(action.iconContentDescription),
                        modifier = Modifier.size(34.dp),
                        tint = MaterialTheme.colorScheme.onPrimary,
                    )
                }
            }

            Spacer(modifier = Modifier.width(28.dp))

            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = stringResource(action.title),
                    color = MaterialTheme.colorScheme.onSurface,
                    style = MaterialTheme.typography.titleLarge,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )

                Spacer(modifier = Modifier.height(10.dp))

                Text(
                    text = stringResource(action.description),
                    color = MaterialTheme.colorScheme.outline,
                    style = MaterialTheme.typography.titleMedium,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis,
                )
            }

            Spacer(modifier = Modifier.width(16.dp))

            Box(
                modifier = Modifier
                    .size(24.dp)
                    .background(action.accent.copy(alpha = 0.12f), RoundedCornerShape(26.dp)),
                contentAlignment = Alignment.Center,
            ) {
                Icon(
                    painter = painterResource(Res.drawable.ic_chevron_right),
                    contentDescription = stringResource(Res.string.chevron_icon_cd),
                    modifier = Modifier.size(24.dp),
                    tint = action.accent,
                )
            }
        }
    }
}

@Composable
private fun manageMemberActions(
    role: UserRole,
    onNewInvitationsClick: () -> Unit,
    onExistingMembersClick: () -> Unit,
): List<ManageMemberAction> {
    val colorScheme = MaterialTheme.colorScheme
    val actions = mutableListOf<ManageMemberAction>()

    if (role == UserRole.ADMIN || role == UserRole.SUPER_ADMIN) {
        actions += ManageMemberAction(
            title = Res.string.new_invitations,
            description = Res.string.new_invitations_description,
            icon = Res.drawable.ic_invitation,
            iconContentDescription = Res.string.new_invitations_icon_cd,
            accent = colorScheme.accentOrange,
            onClick = onNewInvitationsClick,
        )
    }

    if (role == UserRole.SUPER_ADMIN) {
        actions += ManageMemberAction(
            title = Res.string.existing_members,
            description = Res.string.existing_members_description,
            icon = Res.drawable.ic_clubs,
            iconContentDescription = Res.string.existing_members_icon_cd,
            accent = colorScheme.accentBlue,
            onClick = onExistingMembersClick,
        )
    }

    return actions
}

private data class ManageMemberAction(
    val title: StringResource,
    val description: StringResource,
    val icon: DrawableResource,
    val iconContentDescription: StringResource,
    val accent: Color,
    val onClick: () -> Unit,
)

@Preview(name = "Manage Members Content - Admin")
@Composable
private fun ManageMembersScreenContentAdminPreview() {
    RainbowClubTheme {
        Surface {
            Box(modifier = Modifier.padding(24.dp)) {
                ManageMembersScreenContent(
                    uiState = ManageMembersUiState(role = UserRole.ADMIN),
                    onBackClick = {},
                    onNewInvitationsClick = {},
                    onExistingMembersClick = {},
                )
            }
        }
    }
}

@Preview(name = "Manage Members Content - Super Admin")
@Composable
private fun ManageMembersScreenContentSuperAdminPreview() {
    RainbowClubTheme {
        Surface {
            Box(modifier = Modifier.padding(24.dp)) {
                ManageMembersScreenContent(
                    uiState = ManageMembersUiState(role = UserRole.SUPER_ADMIN),
                    onBackClick = {},
                    onNewInvitationsClick = {},
                    onExistingMembersClick = {},
                )
            }
        }
    }
}
